/**
 * AuditPage — Sprint 5.7 (Logs + Auditoria + Timeline).
 *
 * Aba "Auditoria" no NOC. Rastreabilidade completa da operação.
 *  - Cards: Eventos hoje · Instalações · Mudanças saúde ·
 *    Templates aplicados · Auto-bind.
 *  - Tabela: Data · Entidade · Ação · Usuário · Origem · Detalhes.
 *  - Filtros: entity_type · action · actor · device.
 *  - Exportação: CSV / JSON (filtros preservados).
 *
 * Polling de 20s.
 */
import { useCallback, useEffect, useState } from "react";
import { exportAudit, listAudit } from "../lib/api.js";
import { formatRelative } from "../lib/format.js";
import { t } from "../i18n/index.js";

const POLL_MS = 20000;

const ENTITY_TYPES = [
  "device", "inventory", "health", "work_order",
  "team", "template_rule", "installer", "admin",
];
const ACTIONS = [
  "create", "update", "delete", "reserve", "install",
  "bind", "health_change", "status_change", "template_apply",
];

const ACTION_STYLE = {
  bind: "text-noc-accent",
  install: "text-noc-ok",
  health_change: "text-noc-warn",
  status_change: "text-noc-accent",
  template_apply: "text-noc-purple",
  delete: "text-noc-err",
};

export default function AuditPage() {
  const [data, setData] = useState({
    logs: [], total: 0, events_today: 0, installs: 0,
    health_changes: 0, templates_applied: 0, auto_binds: 0,
  });
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [filters, setFilters] = useState({
    entity_type: "", action: "", actor: "", device_id: "",
  });

  const load = useCallback((signal) => {
    const active = {};
    for (const [k, v] of Object.entries(filters)) {
      if (v) active[k] = v;
    }
    listAudit(active, { signal })
      .then((d) => { setData(d); setErr(null); })
      .catch((e) => {
        if (e?.name !== "AbortError") setErr(e?.message || String(e));
      })
      .finally(() => setLoading(false));
  }, [filters]);

  useEffect(() => {
    const ac = new AbortController();
    setLoading(true);
    load(ac.signal);
    const id = setInterval(() => {
      const pollAc = new AbortController();
      load(pollAc.signal);
    }, POLL_MS);
    return () => {
      ac.abort();
      clearInterval(id);
    };
  }, [load]);

  const setField = (k, v) =>
    setFilters((prev) => ({ ...prev, [k]: v }));

  async function doExport(fmt) {
    try {
      const active = { format: fmt };
      for (const [k, v] of Object.entries(filters)) {
        if (v) active[k] = v;
      }
      const blob = await exportAudit(active);
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `audit-export.${fmt}`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      setErr(e?.message || String(e));
    }
  }

  return (
    <div className="p-4 space-y-3">
      <div className="flex items-baseline justify-between gap-2 flex-wrap">
        <h1 className="text-2xl font-bold tracking-tight text-noc-text">
          {t("audit.title")}
        </h1>
        <div className="flex gap-2">
          <button
            className="text-xs px-2.5 py-1 rounded border border-noc-border
                       hover:bg-noc-bg/40 text-noc-accent"
            onClick={() => doExport("csv")}
          >
            {t("audit.exportCsv")}
          </button>
          <button
            className="text-xs px-2.5 py-1 rounded border border-noc-border
                       hover:bg-noc-bg/40 text-noc-accent"
            onClick={() => doExport("json")}
          >
            {t("audit.exportJson")}
          </button>
        </div>
      </div>

      {/* Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
        <DashCard label={t("audit.cardEventsToday")}
                  value={data.events_today} tone="cyan" />
        <DashCard label={t("audit.cardInstalls")}
                  value={data.installs} tone="emerald" />
        <DashCard label={t("audit.cardHealthChanges")}
                  value={data.health_changes} tone="amber" />
        <DashCard label={t("audit.cardTemplates")}
                  value={data.templates_applied} tone="purple" />
        <DashCard label={t("audit.cardAutoBind")}
                  value={data.auto_binds} tone="sky" />
      </div>

      {/* Filtros */}
      <div className="flex gap-2 flex-wrap items-end">
        <FilterSelect
          label={t("audit.filterEntity")}
          value={filters.entity_type}
          onChange={(v) => setField("entity_type", v)}
          options={ENTITY_TYPES}
          tkey="audit.entity_"
        />
        <FilterSelect
          label={t("audit.filterAction")}
          value={filters.action}
          onChange={(v) => setField("action", v)}
          options={ACTIONS}
          tkey="audit.action_"
        />
        <div>
          <label className="text-[10px] text-noc-muted block mb-0.5">
            {t("audit.filterActor")}
          </label>
          <input
            value={filters.actor}
            onChange={(e) => setField("actor", e.target.value)}
            className="text-xs bg-noc-bg/60 border border-noc-border
                       rounded px-2 py-1 text-noc-text"
          />
        </div>
        <div>
          <label className="text-[10px] text-noc-muted block mb-0.5">
            {t("audit.filterDevice")}
          </label>
          <input
            value={filters.device_id}
            onChange={(e) => setField("device_id", e.target.value)}
            className="text-xs bg-noc-bg/60 border border-noc-border
                       rounded px-2 py-1 text-noc-text font-mono"
          />
        </div>
      </div>

      {err && (
        <div className="text-xs text-noc-err bg-noc-err/10 border
                        border-noc-err/30 rounded p-2">
          {err}
        </div>
      )}

      {/* Tabela */}
      <div className="overflow-x-auto card-pad">
        <table className="w-full text-xs">
          <thead className="text-noc-muted border-b border-noc-border">
            <tr>
              <th className="text-left font-medium py-1.5 px-2">
                {t("audit.colDate")}
              </th>
              <th className="text-left font-medium py-1.5 px-2">
                {t("audit.colEntity")}
              </th>
              <th className="text-left font-medium py-1.5 px-2">
                {t("audit.colAction")}
              </th>
              <th className="text-left font-medium py-1.5 px-2">
                {t("audit.colActor")}
              </th>
              <th className="text-left font-medium py-1.5 px-2">
                {t("audit.colSource")}
              </th>
              <th className="text-left font-medium py-1.5 px-2">
                {t("audit.colDetails")}
              </th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={6} className="text-sm text-noc-muted/70
                italic py-3 px-2">{t("common.loading")}</td></tr>
            )}
            {!loading && data.logs.length === 0 && (
              <tr><td colSpan={6} className="text-sm text-noc-muted/60
                italic py-3 px-2">{t("audit.empty")}</td></tr>
            )}
            {data.logs.map((log) => (
              <tr key={log.id}
                  className="border-b border-noc-border/40
                             hover:bg-noc-bg/30">
                <td className="py-1.5 px-2 text-noc-muted whitespace-nowrap">
                  {formatRelative(log.created_at)}
                </td>
                <td className="py-1.5 px-2">
                  <span className="text-noc-text">
                    {t("audit.entity_" + log.entity_type)
                      || log.entity_type}
                  </span>
                  {log.entity_id && (
                    <span className="text-noc-muted/70 font-mono
                                     text-[10px] ml-1">
                      {log.entity_id}
                    </span>
                  )}
                </td>
                <td className={"py-1.5 px-2 "
                  + (ACTION_STYLE[log.action] || "text-noc-text")}>
                  {t("audit.action_" + log.action) || log.action}
                </td>
                <td className="py-1.5 px-2">{log.actor || "—"}</td>
                <td className="py-1.5 px-2 text-noc-muted">
                  {log.source || "—"}
                </td>
                <td className="py-1.5 px-2 text-noc-muted/80">
                  {log.notes || "—"}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="text-[10px] text-noc-muted/70">
        {t("audit.showing")}: {data.logs.length}
      </div>
    </div>
  );
}

function DashCard({ label, value, tone }) {
  const toneCls = {
    cyan: "border-noc-accent/30 text-noc-accent",
    emerald: "border-noc-ok/30 text-noc-ok",
    amber: "border-noc-warn/30 text-noc-warn",
    purple: "border-noc-purple/30 text-noc-purple",
    sky: "border-noc-accent/30 text-noc-accent",
  }[tone] || "border-noc-border text-noc-text";
  return (
    <div className={`rounded border ${toneCls} bg-noc-bg/30 p-2`}>
      <div className="font-mono text-lg font-bold">{value}</div>
      <div className="font-mono text-[10px] uppercase tracking-[0.14em] text-noc-faint">{label}</div>
    </div>
  );
}

function FilterSelect({ label, value, onChange, options, tkey }) {
  return (
    <div>
      <label className="text-[10px] text-noc-muted block mb-0.5">
        {label}
      </label>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="text-xs bg-noc-bg/60 border border-noc-border
                   rounded px-2 py-1 text-noc-text"
      >
        <option value="">— todos —</option>
        {options.map((o) => (
          <option key={o} value={o}>{t(tkey + o) || o}</option>
        ))}
      </select>
    </div>
  );
}
